import { ulid } from "ulid";
import ISO8601 from "@/util/iso-dates";

export const COACH_ACTOR = "coach";

export enum ActivityObjectType {
    JUMP = "JUMP",
    VIDEO = "VIDEO",
    DOCUMENT = "DOCUMENT",
    URL = "URL",
    MESSAGE = "MESSAGE",
    PLACEHOLDER = "PLACEHOLDER",
    REPORT = "REPORT"
}

export enum ReactionType {
    APPLAUSE = "APPLAUSE",
    LOVE = "LOVE",
    LIKE = "LIKE",
    DISLIKE = "DISLIKE",
    GRATEFUL = "GRATEFUL",
    HAPPY = "HAPPY",
    LAUGH = "LAUGH",
    SURPRISE = "SURPRISE",
}

export type Reaction = {
    type: ReactionType;
    actor: string;
}

export interface ActivityObjectBase {
    reactions?: Reaction[];
}

// Declared as a convenience when creating an activity
export interface JumpObject extends ActivityObjectBase {
    title: string;              // title of the jump    
    description?: string;       // optional description of the jump
}

// Declared as a convenience when creating an activity
export interface MessageObject extends ActivityObjectBase {
    text: string;               // text of the message
    noInteraction?: boolean;
    waiting?: boolean;
}

// Declared as a convenience when creating an activity
export interface UrlObject extends ActivityObjectBase {
    url: string;               
}

// Declared as a convenience when creating an activity
export interface DocumentObject extends ActivityObjectBase {
    id: string;                 // ID in the assets table
    title?: string;             // optional title of the document  
    description?: string;       // optional description of the document
}

// Declared as a convenience when creating an activity
export interface VideoObject extends ActivityObjectBase {
    id: string;                 // ID in the assets table
    playbackId: string;         // Mux playback ID  
    title?: string;             // optional title of the video
}

// Combines all convenience interfaces above, but with all properties optional.
// This should be used when serializing to/from the object field in the activities  
// db table and when serializing to/from API requests and responses.
export interface ActivityObject extends ActivityObjectBase {
    id?: string;            // id in assets table; required for asset objects
    title?: string;         // optional for asset objects; required for jump and report objects
    description?: string;   // optional for jump, required for report, document and asset objects
    text?: string;          // required for message and report objects
    noInteraction?: boolean; // true if the activity should not allow replies or reactions
    waiting?: boolean;
    url?: string;           // optional for message and document objects
    playbackId?: string;    // required for video objects
}

export interface ActivityBase {
    id: string;
    actor: string;
    type: ActivityObjectType;
    object: ActivityObject;
    created: string;
    updated: string;
}

export class Activity implements ActivityBase {
    id: string = ulid();
    target: string = this.id;
    rootId: string = this.id;
    actor: string;
    type: ActivityObjectType;
    object: ActivityObject;
    created: string;
    updated: string;

    constructor (
        id: string,
        target: string,
        rootId: string,
        actor: string,
        type: ActivityObjectType,
        object: ActivityObject,
        created: string = ISO8601.nowUTC(),
        updated: string = ISO8601.nowUTC()
    ) {
        this.id = id;
        this.target = target;
        this.rootId = rootId;
        this.type = type;
        this.actor = actor;
        this.object = object;
        this.created = created;
        this.updated = updated; 
    }
}

export type ActivityStream = Array<Activity>;

// Factory functions to create activities  

export const createJumpActivity = (jump: JumpObject, actor: string) => {
    const id = ulid();
    const result = new Activity(
        id,
        id,
        id,
        actor,
        ActivityObjectType.JUMP,
        jump,
        ISO8601.nowUTC(),
        ISO8601.nowUTC()
    );
    return result;
}

export const createMessageActivity = (message: MessageObject, actor: string, rootId: string, target: string) => {  
    const id = ulid();
    const result = new Activity(
        id,
        target,
        rootId,
        actor,
        ActivityObjectType.MESSAGE,
        message,
        ISO8601.nowUTC(),
        ISO8601.nowUTC()
    );
    return result;
}

export const createPlaceholderActivity = (actor: string, rootId: string, target: string) => {  
    const id = ulid();
    const result = new Activity(
        id,
        target,
        rootId,
        actor,
        ActivityObjectType.PLACEHOLDER,
        { noInteraction: true, waiting: true },
        ISO8601.nowUTC(),
        ISO8601.nowUTC()
    );
    return result;
}

export const createVideoActivity = (video: VideoObject, actor: string, rootId: string) => {
    const id = ulid();
    const result = new Activity(
        id,
        rootId,
        rootId,
        actor,
        ActivityObjectType.VIDEO,
        video
    );
    return result;
}

export const createDocumentActivity = (document: DocumentObject, actor: string, rootId: string) => {
    const id = ulid();
    const result = new Activity(
        id,
        rootId,
        rootId,
        actor,
        ActivityObjectType.DOCUMENT,
        document,
        ISO8601.nowUTC(),
        ISO8601.nowUTC()
    );
    return result;
}   

